/**
 * PUE (personalized user experience) Phase 3: "Complete your setup" — the
 * complementary-products rail in the cart drawer and on the cart page
 * (both carry an empty `[data-pag-cart-complements]` container).
 *
 * Loads eagerly (cart state needs no interaction to know), not on drawer
 * open, so the rail is never the thing a shopper watches populate. Refetches
 * whenever the cart itself changes — including a shopper adding one of its
 * own items — via Shopify's standard cart event, the same one every other
 * cart-aware component in this theme already uses, so no second cart system
 * gets invented here.
 */

import { StandardEvents } from '@shopify/events';

const SECTION_ID = 'pag-cart-complements';
const SELECTOR = '[data-pag-cart-complements]';

async function fetchCart() {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * @param {string} productId
 * @param {'complementary'} intent
 * @returns {Promise<string>} trimmed HTML, empty string if there is nothing to show
 */
async function fetchComplementsHtml(productId, intent) {
  const url = new URL('/recommendations/products', window.location.origin);
  url.searchParams.set('product_id', productId);
  url.searchParams.set('intent', intent);
  url.searchParams.set('limit', '6');
  url.searchParams.set('section_id', SECTION_ID);

  try {
    const response = await fetch(url);
    if (!response.ok) return '';
    return (await response.text()).trim();
  } catch {
    return '';
  }
}

/** @param {HTMLElement} container */
async function load(container) {
  const cart = await fetchCart();

  if (!cart || cart.items.length === 0) {
    container.hidden = true;
    container.replaceChildren();
    return;
  }

  const productId = String(cart.items[0].product_id);

  // Picks come from the engine's metafields on the cart items, read in Liquid; the request
  // only gives the section a cart to read. Empty response = nothing to show.
  const html = await fetchComplementsHtml(productId, 'complementary');

  if (!html) {
    container.hidden = true;
    container.replaceChildren();
    return;
  }

  container.innerHTML = html;
  container.hidden = false;
  // <product-form-component>/<add-to-cart-component> inside the new markup
  // upgrade themselves automatically (registered custom elements do, however
  // they entered the DOM); this is only for anything else listening for a
  // freshly-hydrated PUE surface.
  container.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
}

function loadAll() {
  document.querySelectorAll(SELECTOR).forEach((container) => load(/** @type {HTMLElement} */ (container)));
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // cart-drawer's own morph replaces this container's contents with a fresh,
  // empty one on every cart change; wait for that to settle before
  // re-querying and populating, or this would write into the instance about
  // to be discarded.
  event.promise?.then(loadAll).catch(() => {});
});

loadAll();
